import { useParams, useSearchParams } from 'react-router-dom';

import {
  useLiveGameLolQuery,
  useLookupLolQuery,
} from '@/services/qriotsApi';
import type { Platform } from '@/types/qriots';
import { classifyQriotsError } from '@/services/qriotsErrors';
import { useIsVisible } from '@/hooks/useIsVisible';
import { Loader } from '@/components/common/Loader';
import { LolProfileCard } from '@/components/riot/LolProfileCard';
import { LiveGameBanner } from '@/components/riot/LiveGameBanner';
import { QriotsErrorState } from '@/components/riot/QriotsErrorState';

export function RiotLolDetail() {
  const { puuid = '' } = useParams<{ puuid: string }>();
  const [search] = useSearchParams();
  const region = (search.get('region') ?? 'EUW1') as Platform;
  const gameName = search.get('gameName') ?? '';
  const tagLine = search.get('tagLine') ?? '';

  const visible = useIsVisible();

  const profile = useLookupLolQuery(
    { gameName, tagLine, region },
    { skip: !gameName || !tagLine },
  );

  const liveGame = useLiveGameLolQuery(
    { puuid, region },
    { pollingInterval: 60_000, skip: !puuid || !visible },
  );

  // not_in_game = 404 = "not playing right now", which we want to render as
  // "no banner" rather than as an error.
  const liveGameError = liveGame.error
    ? classifyQriotsError(liveGame.error)
    : null;
  const liveGameIsNotPlaying = liveGameError?.kind === 'not_found';

  if (profile.isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (profile.error) {
    const { kind, retryAfterSec } = classifyQriotsError(profile.error);
    return (
      <QriotsErrorState
        kind={kind}
        retryAfterSec={retryAfterSec}
        onRetry={profile.refetch}
      />
    );
  }

  if (!profile.data) return null;

  return (
    <div className="flex flex-col gap-4 pb-6 pt-4">
      {liveGame.data && !liveGameIsNotPlaying && (
        <LiveGameBanner data={liveGame.data} viewerPuuid={puuid} />
      )}
      <LolProfileCard data={profile.data} />
    </div>
  );
}
